// API URL: use runtime config (config.js) if available, fallback to same-origin /api
const API_URL = window.CHATBOT_CONFIG?.API_URL
  || `http://${window.location.hostname}:5500/api`;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('chatToken');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('chatToken', token);
    } else {
      localStorage.removeItem('chatToken');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('chatToken');
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.setToken(null);
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || 'Request failed');
    }

    // Handle empty responses (204 No Content or empty body)
    const text = await response.text();
    if (!text) return null;
    
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  // Auth
  async login(username, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  logout() {
    this.setToken(null);
  }

  // Users
  async getUsers(currentUserId) {
    const params = currentUserId ? `?currentUserId=${currentUserId}` : '';
    return this.request(`/chat/users${params}`);
  }

  // Departments
  async getDepartments(currentUserId) {
    const params = currentUserId ? `?currentUserId=${currentUserId}` : '';
    return this.request(`/chat/departments${params}`);
  }

  // Messages
  async getDirectMessages(otherUserId, currentUserId) {
    const params = currentUserId ? `?currentUserId=${currentUserId}` : '';
    return this.request(`/chat/messages/direct/${otherUserId}${params}`);
  }

  async getDepartmentMessages(departmentId, currentUserId) {
    const params = currentUserId ? `?currentUserId=${currentUserId}` : '';
    return this.request(`/chat/messages/department/${departmentId}${params}`);
  }

  async sendMessage(dto, senderId) {
    const params = senderId ? `?senderId=${senderId}` : '';
    return this.request(`/chat/messages${params}`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async markAsRead(messageId, userId) {
    const params = userId ? `?userId=${userId}` : '';
    return this.request(`/chat/messages/${messageId}/read${params}`, {
      method: 'POST',
    });
  }

  async markAllAsRead(otherUserId, departmentId, userId) {
    const params = new URLSearchParams();
    if (otherUserId) params.append('otherUserId', otherUserId);
    if (departmentId) params.append('departmentId', departmentId);
    if (userId) params.append('userId', userId);
    return this.request(`/chat/messages/read-all?${params}`, {
      method: 'POST',
    });
  }

  async getUnreadCounts(userId) {
    const params = userId ? `?userId=${userId}` : '';
    return this.request(`/chat/unread-counts${params}`);
  }

  // Status
  async updateStatus(status, statusText = null) {
    return this.request('/chat/status', {
      method: 'POST',
      body: JSON.stringify({ status, statusText }),
    });
  }

  async getStatuses() {
    return this.request('/chat/statuses');
  }

  // Delete messages
  async deleteMessages(targetUserId, targetDepartmentId, dateFrom, dateTo, deleteAll) {
    return this.request('/chat/messages/delete', {
      method: 'POST',
      body: JSON.stringify({
        targetUserId,
        targetDepartmentId,
        dateFrom,
        dateTo,
        deleteAll
      }),
    });
  }

  // External Auth (SSO)
  async externalLogin(data) {
    const response = await this.request('/auth/external', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response && response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  // File Upload
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/file/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  // Avatar Upload
  async uploadAvatar(formData) {
    const token = this.getToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/chat/profile/avatar`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  async deleteAvatar() {
    return this.request('/chat/profile/avatar', {
      method: 'DELETE',
    });
  }

  // Department members
  async getDepartmentMembers(departmentId) {
    return this.request(`/chat/departments/${departmentId}/members`);
  }
}

export const apiService = new ApiService();
export default apiService;
